import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRingIcon, CameraIcon, ClockIcon, MapPinIcon, XIcon } from 'lucide-react';
import type { Pet } from '../../data/animaux';
interface LostFlowProps {
  pet: Pet;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}
type Step = 'lieu' | 'moment' | 'photo' | 'zone' | 'revue';
const places = ['Lakeshore Drive Park', '5108 - 46 Street', 'Current location'];
const moments = ['Today · 3:20 p.m.', 'Today · this morning', 'Last night'];
export function LostFlow({
  pet,
  open,
  onClose,
  onConfirm
}: LostFlowProps) {
  const [step, setStep] = useState<Step>('lieu');
  const [place, setPlace] = useState(places[0]);
  const [moment, setMoment] = useState(moments[0]);
  const [radius, setRadius] = useState(1.5);
  const choiceClass = (active: boolean) => `rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft text-ville-indigo' : 'border-slate-200 text-ville-navy hover:border-ville-indigo'}`;
  return <AnimatePresence data-id="element-115" data-mp="fd31ri703klh">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-116" data-mp="j87pibi7zuc">
          <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-117" data-mp="y6gqh5k4eusn" />

          <motion.div role="dialog" aria-modal="true" aria-label={`${pet.name} is missing`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-118" data-mp="jfzptofb88wt">
            <div className="flex items-start justify-between gap-4" data-id="element-119" data-mp="1ludfp12tp0hi">
              <div data-id="element-120" data-mp="1hrgy9dp0raf5">
                <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-121" data-mp="1hqnw6q10993c">{pet.name} is missing</h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-122" data-mp="1cruj2ypmrk3p">
                  We will help you notify people nearby.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-123" data-mp="1332u8zfv9fx0">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-124" data-mp="k3ylzsw5fq6e" />
              </button>
            </div>

            <div className="mt-6" data-id="element-125" data-mp="2vrah4vgtno8v">
              {step === 'lieu' && <div data-id="element-126" data-mp="2vtejva2zih7k">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-127" data-mp="30qeuzzeoya5j">
                    <MapPinIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-128" data-mp="35phw58rjvw88" />
                    Where was {pet.name} last seen?
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-129" data-mp="39qa8wbzmsj99">
                    {places.map(option => <li key={option} data-id="element-130" data-mp="2cypuhvrpufdi">
                        <button type="button" onClick={() => setPlace(option)} className={choiceClass(place === option)} data-id="element-131" data-mp="2hzdwtdleain1">
                          {option}
                        </button>
                      </li>)}
                  </ul>
                </div>}

              {step === 'moment' && <div data-id="element-132" data-mp="1u9tackq19390">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-133" data-mp="1u88fetzt3iam">
                    <ClockIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-134" data-mp="1y9tmtztkn4df" />
                    When?
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-135" data-mp="1a8qbz97p976c">
                    {moments.map(option => <li key={option} data-id="element-136" data-mp="1oxcoz49utmue">
                        <button type="button" onClick={() => setMoment(option)} className={choiceClass(moment === option)} data-id="element-137" data-mp="1oyfh1fiwxvbw">
                          {option}
                        </button>
                      </li>)}
                  </ul>
                </div>}

              {step === 'photo' && <div data-id="element-138" data-mp="27vozougez4s3">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-139" data-mp="27w8h8a25jr5l">
                    <CameraIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-140" data-mp="22wm749hmer10" />
                    Recent photo
                  </p>
                  <div className="mt-4 flex items-center gap-4 rounded-2xl border border-ville-indigo bg-ville-indigo-soft p-4" data-id="element-141" data-mp="dk1wokndby0m">
                    <img src={pet.photo} alt={pet.name} className="h-20 w-20 rounded-2xl object-cover" data-id="element-142" data-mp="2k6gdjlq5jm6v" />
                    <p className="text-sm text-ville-navy" data-id="element-143" data-mp="2p5sxnypn2f5r">Photo from {pet.name}’s profile</p>
                  </div>
                  <button type="button" className="mt-3 text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-ville-navy" data-id="element-144" data-mp="2ah6grskd5fcf">
                    Choose another photo
                  </button>
                </div>}

              {step === 'zone' && <div data-id="element-145" data-mp="25f7fco86osi4">
                  <p className="text-[15px] font-bold text-ville-navy" data-id="element-146" data-mp="1rhskv532puja">Search area</p>
                  <div className="relative mt-4 h-44 overflow-hidden rounded-2xl bg-slate-100" data-id="element-147" data-mp="1rjviaa6k2py1">
                    <div className="absolute inset-0 opacity-60 [background-image:linear-gradient(#cbd5e1_1px,transparent_1px),linear-gradient(90deg,#cbd5e1_1px,transparent_1px)] [background-size:28px_28px]" data-id="element-148" data-mp="1wigkgf5dbjp4" />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ville-indigo bg-ville-indigo/10" style={{
                width: `${60 + radius * 40}px`,
                height: `${60 + radius * 40}px`
              }} data-id="element-149" data-mp="3pdhkwb5jiq4k" />
                    <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ville-indigo" data-id="element-150" data-mp="3ag8gafr87puw" />
                  </div>
                  <p className="mt-3 text-sm text-slate-600" data-id="element-151" data-mp="by1mvaoclc8w">
                    <span className="font-bold text-ville-navy" data-id="element-152" data-mp="6z8a5kgvxp9s">Last known location:</span> {place}
                  </p>
                  <label htmlFor="radius" className="mt-4 block text-sm font-semibold text-slate-600" data-id="element-153" data-mp="27gtc1nj6xwu">
                    Notification radius: {radius.toFixed(1)} km
                  </label>
                  <input id="radius" type="range" min={0.5} max={3} step={0.5} value={radius} onChange={event => setRadius(Number(event.target.value))} className="mt-2 w-full accent-[#8a6212]" data-id="element-154" data-mp="uq486zbxpgph" />
                  <p className="mt-2 text-xs text-slate-500" data-id="element-155" data-mp="1775a9oswt5js">
                    This area helps reach the people who may have seen {pet.name}. It does not show where the pet is now.
                  </p>
                </div>}

              {step === 'revue' && <div data-id="element-156" data-mp="1l7g2ow9vas72">
                  <p className="text-sm font-bold uppercase tracking-wider text-ville-indigo" data-id="element-157" data-mp="1l6o7hmk6mwhb">The alert is ready</p>
                  <div className="mt-4 flex items-center gap-4 rounded-2xl border border-slate-200 p-4" data-id="element-158" data-mp="1pz7neq6ce3pa">
                    <img src={pet.photo} alt="" className="h-16 w-16 rounded-xl object-cover" data-id="element-159" data-mp="1uvy156uocn7j" />
                    <div data-id="element-160" data-mp="1zxnbsldziyv3">
                      <p className="font-extrabold text-ville-navy" data-id="element-161" data-mp="1zu991c1esnn4">{pet.name}</p>
                      <p className="text-sm text-slate-500" data-id="element-162" data-mp="24vyl8v0db7cs">
                        {pet.breed} · {place}
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-163" data-mp="2q88akyrrzjnb">{moment}</p>
                    </div>
                  </div>
                  <p className="mt-4 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-id="element-164" data-mp="2z6tr6odosolg">
                    <BellRingIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-165" data-mp="2u7qqfg4qp4ja" />
                    A quiet notice will be sent by web and mobile notification to residents who opted in, within{' '}
                    {radius.toFixed(1)} km. Your personal contact details are never shared.
                  </p>
                </div>}
            </div>

            <div className="mt-8 flex items-center justify-between gap-4" data-id="element-166" data-mp="2bh92douuqx49">
              <button type="button" onClick={() => {
            const order: Step[] = ['lieu', 'moment', 'photo', 'zone', 'revue'];
            const index = order.indexOf(step);
            if (index === 0) onClose();else setStep(order[index - 1]);
          }} className="text-sm font-semibold text-slate-500 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-167" data-mp="1nrn6ba9s0y0h">
                {step === 'lieu' ? 'Cancel' : 'Back'}
              </button>
              <button type="button" onClick={() => {
            const order: Step[] = ['lieu', 'moment', 'photo', 'zone', 'revue'];
            const index = order.indexOf(step);
            if (step === 'revue') {
              onConfirm();
              setStep('lieu');
              return;
            }
            setStep(order[index + 1]);
          }} className={`rounded-full px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${step === 'revue' ? 'bg-red-600 focus-visible:ring-red-600' : 'bg-ville-navy focus-visible:ring-ville-indigo'}`} data-id="element-168" data-mp="3qc7qsf9ee4cd">
                {step === 'revue' ? `Report ${pet.name} missing` : 'Continue'}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}